import { Injectable } from '@angular/core';
import * as dayjs from 'dayjs';
import * as isBetween from 'dayjs/plugin/isBetween';
import * as isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import * as isToday from 'dayjs/plugin/isToday';
import * as timezone from 'dayjs/plugin/timezone'; // dependent on utc plugin
import * as utc from 'dayjs/plugin/utc';
import { Subject, Subscription } from 'rxjs';
import { RepositoryService } from '../baseServices/repository.service';
import { ActivityLogConstants, ActivityLogStatusHierarchy, ActivityLogStatusNumber } from '../constant/activity-log-constants';
import { GlobalConstants } from '../constant/constant';
import {
  ActivityLogFilterDataDto,
  ApproveActivityLogDataDto,
  BaseActivityLogDataDto,
  CreateOpenActivityLogDataDto,
  CreateOpenActivityLogOrReopenDto,
  CreateSubmittedActivityLogDataDto,
  CreateVerifiedActivityLogDataDto,
  OpenActivityLogDataDto,
  ReturnActivityLogDataDto,
  SubmitActivityLogDataDto,
  VerifyActivityLogDataDto
} from '../dtos/activity-log-service-dtos';
import { ActivityLog } from '../entities/ActivityLog';
import { StatusLookup } from '../entities/StatusLookup';
import { StoreName } from '../entities/StoreName';
import { DataChangeEmitPayload, DataChangeEmitWhat, DataChangeEmitWho } from '../models/data-change-emit/data-change-emit-payload';
import { DbKeyType } from '../models/db/db-store';
import { DbTransaction } from '../models/db/db-transaction';
import { Callback, CallbackAddMode, CallbackNames } from '../models/db/db-transaction-callback';
import { ArrayHelper } from '../utils/array-helper';
import { DateTimeHelper, DateTimeType } from '../utils/date-time-helper';
import { Check, EntityHelper } from '../utils/entity-helper';
import { ActionValidationException, ConflictValidationException, RecordNotFoundException, RecordSoftDeletedException, StateValidationException, ValidationException } from '../utils/exceptions';
import { UserService } from './user.service';
import { StatusLookupService } from './status-lookup.service';
import { EntityTypeService } from './entity-type.service';
import { EventBusService } from './event-bus.service';
import { AppEventType } from '../enums/app-events.enum';
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isToday);
dayjs.extend(isBetween);
dayjs.extend(isSameOrAfter);

@Injectable({
  providedIn: 'root',
})

export class ActivityLogService {
  private emitChangeSource = new Subject<DataChangeEmitPayload<ActivityLog>>();
  changeEmitted$ = this.emitChangeSource.asObservable();

  private statuses: StatusLookup[] = [];
  private initialSyncDataSubscription: Subscription;
  constructor(private repository: RepositoryService, private entityTypeService: EntityTypeService, private userService: UserService, private statusLookupService: StatusLookupService, private eventBusService: EventBusService) {
    this.initialSyncDataSubscription = this.eventBusService.on(AppEventType.INITIAL_SYNC_DATA_AVAILABLE, () => this.loadStatuses(null));
  }

  // #region Utility

  /**
   * Loads the statuses for activity logs into memory.
   * @param existingTransaction - Optional. Database transaction to use.
   * @returns A promise that resolves when the statuses have been loaded.
   */
  async loadStatuses(existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const activityLogEntityTypeId = await this.entityTypeService.getEntityTypeIdByNumber('ActivityLog', transaction);
    const statusLookups = await this.statusLookupService.getAllStatusLookups(undefined, transaction);

    this.statuses = statusLookups.filter(status => status.EntityTypeId === activityLogEntityTypeId);
  }

  emitChange(activityLog: ActivityLog, who: DataChangeEmitWho, what: DataChangeEmitWhat): void {
    this.emitChangeSource.next({ data: activityLog, who, what });
  }

  private getEmitChangeCallback(activityLog: ActivityLog, who: DataChangeEmitWho, what: DataChangeEmitWhat): Callback {
    return {
      name: CallbackNames.EmitChangeForActivityLog + '_' + activityLog.RecordId,
      fn: this.emitChange.bind(this, activityLog, who, what),
    };
  }

  private getValidateActivityLogCallback(activityLog: ActivityLog): Callback {
    return {
      name: CallbackNames.ValidateActivityLog,
      fn: this.validateActivityLog.bind(this, activityLog),
    };
  }

  private async startTransactionIfNeeded(existingTransaction?: DbTransaction): Promise<DbTransaction> {
    return existingTransaction || (await this.repository.startTransaction([StoreName.ActivityLog, StoreName.EntityType, StoreName.StatusLookup], 'readwrite'));
  }

  // #endregion
  // #region Low-level Get One

  async getActivityLogByRecordId(recordId: number, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<ActivityLog> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const result = await this.repository.getByLocalId<ActivityLog>(StoreName.ActivityLog, recordId, ActivityLog, transaction);

    if (result === null) {
      throw new RecordNotFoundException(recordId);
    }

    const record = result as ActivityLog;

    if (this.checkIsActivityLogSoftDeleted(record) && !includeDeleted) {
      throw new RecordSoftDeletedException(recordId);
    }

    return record;
  }

  async getActivityLogByOnlineId(onlineId: number, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<ActivityLog> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const result = await this.repository.getByOnlineId<ActivityLog>(StoreName.ActivityLog, onlineId, ActivityLog, transaction);

    if (result === null) {
      throw new RecordNotFoundException(onlineId);
    }

    const record = result as ActivityLog;

    if (this.checkIsActivityLogSoftDeleted(record) && !includeDeleted) {
      throw new RecordSoftDeletedException(onlineId);
    }

    return record;
  }

  async getActivityLogByUserIdProjectIdDate(
    userId: number,
    projectId: number,
    activityDate: DateTimeType,
    existingTransaction?: DbTransaction,
    includeDeleted: boolean = false
  ): Promise<ActivityLog | null> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const startDate = DateTimeHelper.formatDateTime(DateTimeHelper.getStartOfDay(activityDate));
    const endDate = DateTimeHelper.formatDateTime(DateTimeHelper.getEndOfDay(activityDate));
    const key: DbKeyType = {
      lower: [userId, projectId, startDate],
      upper: [userId, projectId, endDate],
    };

    let result = await this.repository.getAllByIndex(StoreName.ActivityLog, GlobalConstants.KEY_NAMES.UserId_ProjectId_Date, key, ActivityLog, transaction);
    let records = result as ActivityLog[];

    if (!includeDeleted) {
      records = records.filter(record => !this.checkIsActivityLogSoftDeleted(record));
    }

    return ArrayHelper.firstOrDefault(records);
  }

  // #endregion
  // #region Low-level Get Many

  async getActivityLogsByUserIds(userIds: number[], existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<ActivityLog[]> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const results = await this.repository.getAllByIndex(StoreName.ActivityLog, GlobalConstants.KEY_NAMES.UserId, userIds, ActivityLog, transaction);
    let records = results as ActivityLog[];

    if (!includeDeleted) {
      records = records.filter(record => !this.checkIsActivityLogSoftDeleted(record));
    }

    return records;
  }

  async getActivityLogsByProjectIds(projectIds: number[], existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<ActivityLog[]> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const results = await this.repository.getAllByIndex(StoreName.ActivityLog, GlobalConstants.KEY_NAMES.ProjectId, projectIds, ActivityLog, transaction);
    let records = results as ActivityLog[];

    if (!includeDeleted) {
      records = records.filter(record => !this.checkIsActivityLogSoftDeleted(record));
    }

    return records;
  }

  async getActivityLogsByUserIdDate(userId: number, date: DateTimeType, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<ActivityLog[]> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const startDate = DateTimeHelper.formatDateTime(DateTimeHelper.getStartOfDay(date));
    const endDate = DateTimeHelper.formatDateTime(DateTimeHelper.getEndOfDay(date));
    const key: DbKeyType = {
      lower: [userId, startDate],
      upper: [userId, endDate],
    };

    const result = await this.repository.getAllByIndex(StoreName.ActivityLog, GlobalConstants.KEY_NAMES.UserId_Date, key, ActivityLog, transaction);
    let records = result as ActivityLog[];

    if (!includeDeleted) {
      records = result.filter(activityLog => !this.checkIsActivityLogSoftDeleted(activityLog));
    }

    return records;
  }

  async getActivityLogsByUserIdsDateRange(userIds: number[], startDate: DateTimeType, endDate: DateTimeType, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<ActivityLog[]> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const formattedStartDate = DateTimeHelper.formatDateTime(DateTimeHelper.getStartOfDay(startDate));
    const formattedEndDate = DateTimeHelper.formatDateTime(DateTimeHelper.getEndOfDay(endDate));
    const key: DbKeyType[] = userIds.map(userId => ({
      lower: [userId, formattedStartDate],
      upper: [userId, formattedEndDate],
    }));

    const result = await this.repository.getAllByIndex(StoreName.ActivityLog, GlobalConstants.KEY_NAMES.UserId_Date, key, ActivityLog, transaction);
    let records = result as ActivityLog[];

    if (!includeDeleted) {
      records = records.filter(activityLog => !this.checkIsActivityLogSoftDeleted(activityLog));
    }

    return records;
  }

  // #endregion
  // #region Low-level Create

  async createActivityLog(who: DataChangeEmitWho, recordToCreate: ActivityLog, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<number> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    if (who === DataChangeEmitWho.Self) {
      // LC1. Validate action
      await this.validateActivityLogCreation(transaction);

      // LC2. Idempotent check
      const existingRecord = await this.getActivityLogByUserIdProjectIdDate(recordToCreate.UserId, recordToCreate.ProjectId, recordToCreate.ActivityDate, transaction);
      if (existingRecord != null) {
        return existingRecord.RecordId;
      }

      // LC3. Validate model
      await this.validateActivityLog(recordToCreate, transaction);
    }
    else {
      transaction.addOnCommitCallback(this.getValidateActivityLogCallback(recordToCreate), CallbackAddMode.PushDuplicate);
    }

    // LC4. Repository call
    const recordId = await this.repository.create(recordToCreate, StoreName.ActivityLog, transaction);

    // LC5. Get created record
    const createdRecord = await this.getActivityLogByRecordId(recordId, transaction, includeDeleted);

    // LC6. Report changes
    transaction.addOnCommitCallback(this.getEmitChangeCallback(createdRecord, who, DataChangeEmitWhat.Create));

    // LC7. Return id
    return recordId;
  }

  // #endregion
  // #region Low-level Update

  async updateActivityLog(who: DataChangeEmitWho, recordToUpdate: ActivityLog, skipLocalCopyValidation: boolean, localCopy: ActivityLog | null, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    if (who === DataChangeEmitWho.Self) {
      // LU1. Get existing record
      const existingRecord = await this.getActivityLogByRecordId(recordToUpdate.RecordId, transaction);

      // LU2. Validate action
      await this.validateActivityLogUpdate(existingRecord, transaction);

      // LU3. Idempotent check
      if (EntityHelper.checkAllFieldsEqual(existingRecord, recordToUpdate)) {
        return;
      }

      // LU4. Local copy validate
      if (!skipLocalCopyValidation) {
        const checks: Check<ActivityLog>[] = [{ name: 'ImmutableFields', function: this.checkImmutableFieldsMatch }];
        EntityHelper.validateLocalCopy<ActivityLog>(localCopy, existingRecord, checks);
      }

      // LU5. Low-level validate
      await this.validateActivityLog(recordToUpdate, transaction);
    }
    else {
      transaction.addOnCommitCallback(this.getValidateActivityLogCallback(recordToUpdate), CallbackAddMode.PushDuplicate);
    }

    // LU6. Repository call
    await this.repository.update(recordToUpdate, StoreName.ActivityLog, transaction);

    // LU7. Get updated record
    const updatedRecord = await this.getActivityLogByRecordId(recordToUpdate.RecordId, transaction, true);

    // LU8. Report changes
    transaction.addOnCommitCallback(this.getEmitChangeCallback(updatedRecord, who, DataChangeEmitWhat.Update));

    // LU9. Return
    return;
  }

  // #endregion
  // #region Low-level Delete

  /**
   * Soft deletes an activity log record by setting the 'IsDeleted' property to true and updating the record in the database.
   * @param recordId - The local ID of the record to delete.
   * @param existingTransaction - Optional. An existing database transaction to be used.
   * @param who - Optional. Specifies who initiated the data change.
   * @returns A promise that resolves when the activity log record is successfully soft deleted.
   */
  public async softDeleteActivityLog(who: DataChangeEmitWho, recordId: number, skipLocalCopyValidation: boolean, localCopy: ActivityLog | null, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // LD1. Get existing record
    const recordToSoftDelete = await this.getActivityLogByRecordId(recordId, transaction);

    // LD2. Validate action
    await this.validateActivityLogSoftDeletion(recordToSoftDelete, transaction);

    // LD3. Idempotent check
    if (this.checkIsActivityLogSoftDeleted(recordToSoftDelete)) {
      return;
    }

    // LD4. Validate local copy
    if (!skipLocalCopyValidation) {
      const checks: Check<ActivityLog>[] = [{ name: 'ImmutableFields', function: this.checkImmutableFieldsMatch }];
      EntityHelper.validateLocalCopy<ActivityLog>(localCopy, recordToSoftDelete, checks);
    }

    // LD5. Prepare changes
    recordToSoftDelete.setSoftDeleted();

    // LD6. Low-level validate
    await this.validateSoftDeletedTimeRecord(recordToSoftDelete, transaction);

    // LD7. Repository call
    await this.repository.update(recordToSoftDelete, StoreName.ActivityLog, transaction);

    // LD8. Get soft deleted record
    const softDeletedRecord = await this.getActivityLogByRecordId(recordId, transaction, true);

    // LD9. Report changes
    transaction.addOnCommitCallback(this.getEmitChangeCallback(softDeletedRecord, who, DataChangeEmitWhat.Delete));

    // LD10. Return
    return;
  }

  /**
   * Deletes an activity log record from the database.
   * @param recordId - The local ID of the record to delete.
   * @param existingTransaction - Optional. An existing database transaction to use.
   * @returns A promise that resolves when the activity log record is successfully hard deleted.
   */
  public async hardDeleteActivityLog(who: DataChangeEmitWho, recordId: number, skipLocalCopyValidation: boolean, localCopy: ActivityLog | null, existingTransaction?: DbTransaction) {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // 1. Idempotent check
    try {
      // 2. Get existing record
      const existingRecord = await this.getActivityLogByRecordId(recordId, transaction, true);

      // 3. Local copy validate
      if (!skipLocalCopyValidation) {
        const checks: Check<ActivityLog>[] = [{ name: 'ImmutableFields', function: this.checkImmutableFieldsMatch }];
        EntityHelper.validateLocalCopy(localCopy, existingRecord, checks);
      }

      // 4. Validate hard delete
      await this.validateActivityLogHardDeletion(existingRecord, transaction);

      // 5. Repository call
      await this.repository.delete(recordId, StoreName.ActivityLog, transaction);

      // 6. Report changes
      transaction.addOnCommitCallback(this.getEmitChangeCallback(existingRecord, who, DataChangeEmitWhat.Delete));

      // 7. Return
      return;
    } catch (e) {
      if (e instanceof RecordNotFoundException) {
        return;
      }
      throw e;
    }
  }

  // #endregion
  // #region Low-level Validate Action

  async validateActivityLogCreation(existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    // Add any specific validation logic here
  }

  async validateActivityLogUpdate(existingRecord: ActivityLog, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    // TODO: Check role claim
    // Add other validation checks as necessary
  }

  async validateActivityLogSoftDeletion(existingRecord: ActivityLog, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    // TODO: Check role claim
    // Add other validation checks as necessary
  }

  async validateActivityLogHardDeletion(existingRecord: ActivityLog, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    // TODO: Check role claim
    // Add other validation checks as necessary
  }

  // #endregion
  // #region Low-level Validate Model

  async validateActivityLog(activityLog: ActivityLog, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const isActivityLogSoftDeleted = this.checkIsActivityLogSoftDeleted(activityLog);
    if (isActivityLogSoftDeleted) {
      return;
    }
    else {
      const existingActivityLog = await this.getActivityLogByUserIdProjectIdDate(activityLog.UserId, activityLog.ProjectId, activityLog.ActivityDate, transaction);
      if (existingActivityLog !== null && !this.checkAnyActivityLogIdentifiersMatch(activityLog, existingActivityLog)) {
        throw new ConflictValidationException(existingActivityLog);
      }
    }

    return;
  }

  async validateSoftDeletedTimeRecord(activityLog: ActivityLog, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    if (!this.checkIsActivityLogSoftDeleted(activityLog)) {
      throw new StateValidationException('SoftDeleted', activityLog);
    }
  }

  // #endregion
  // #region Low-level Sync

  // #endregion
  // #region Mid-level Get One

  async getOpenActivityLog(userId: number, projectId: number, activityDate: DateTimeType, existingTransaction?: DbTransaction): Promise<ActivityLog | null> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const existingActivityLog = await this.getActivityLogByUserIdProjectIdDate(userId, projectId, activityDate, transaction);

    if (existingActivityLog === null) {
      return null;
    }

    if (await this.checkIsActivityLogOpen(existingActivityLog, transaction)) {
      return null;
    }

    return existingActivityLog;
  }

  // #endregion
  // #region Mid-level Get Many

  // #endregion
  // #region Mid-level Create

  async createOpenActivityLog(who: DataChangeEmitWho, data: CreateOpenActivityLogDataDto, existingTransaction?: DbTransaction): Promise<number> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // MC1. Validate action
    await this.validateOpenActivityLogCreation(data, transaction);

    // MC2. Build model
    const recordToCreate = await this.buildOpenActivityLogDto(data as OpenActivityLogDataDto, data as BaseActivityLogDataDto, null, transaction);

    // MC3. Idempotent check
    const existingRecord = await this.getOpenActivityLog(data.UserId, data.ProjectId, data.ActivityDate, transaction);
    if (existingRecord !== null) {
      return existingRecord.RecordId;
    }

    // MC4. Low-level create
    const createdRecordId = await this.createActivityLog(who, recordToCreate as ActivityLog, transaction);

    // MC5. Return id
    return createdRecordId;
  }

  // #endregion
  // #region Mid-level Update

  async reopenActivityLog(
    who: DataChangeEmitWho,
    recordId: number,
    data: OpenActivityLogDataDto,
    skipLocalCopyValidation: boolean,
    localCopy: ActivityLog | null,
    existingTransaction?: DbTransaction
  ): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // MU1. Get existing record
    const existingRecord = await this.getActivityLogByRecordId(recordId, transaction);

    // MU2. Validate action
    await this.validateActivityLogReopen(data, existingRecord, true, transaction);

    // MU3. Prepare changes
    const recordToUpdate = await this.buildOpenActivityLogDto(data, null, existingRecord, transaction);

    // MU4. Idempotent check
    if (await this.checkIsActivityLogOpen(existingRecord, transaction)) {
      return;
    }

    // MU5. Validate local copy
    if (!skipLocalCopyValidation) {
      const checks = [{ name: 'ImmutableFields', function: this.checkImmutableFieldsMatch }];
      EntityHelper.validateLocalCopy(localCopy, existingRecord, checks);
    }

    // MU6. Low-level update
    await this.updateActivityLog(who, recordToUpdate, skipLocalCopyValidation, localCopy, transaction);

    // MU7. Return
    return;
  }

  async returnActivityLog(
    who: DataChangeEmitWho,
    recordId: number,
    data: ReturnActivityLogDataDto,
    skipLocalCopyValidation: boolean,
    localCopy: ActivityLog | null,
    existingTransaction?: DbTransaction
  ): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // MU1. Get existing record
    const existingRecord = await this.getActivityLogByRecordId(recordId, transaction);

    // MU2. Validate action
    await this.validateActivityLogReturn(data, existingRecord, true, transaction);

    // MU3. Prepare changes
    const recordToUpdate = await this.buildReturnedActivityLogDto(data, null, existingRecord, transaction);

    // MU4. Idempotent check
    if (await this.checkIsActivityLogReturned(existingRecord, transaction)) {
      return;
    }

    // MU5. Validate local copy
    if (!skipLocalCopyValidation) {
      const checks = [{ name: 'ImmutableFields', function: this.checkImmutableFieldsMatch }];
      EntityHelper.validateLocalCopy(localCopy, existingRecord, checks);
    }

    // MU6. Low-level update
    await this.updateActivityLog(who, recordToUpdate, skipLocalCopyValidation, localCopy, transaction);

    // MU7. Return
    return;
  }

  async submitActivityLog(
    who: DataChangeEmitWho,
    recordId: number,
    data: SubmitActivityLogDataDto,
    skipLocalCopyValidation: boolean,
    localCopy: ActivityLog | null,
    existingTransaction?: DbTransaction
  ): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // MU1. Get existing record
    const existingRecord = await this.getActivityLogByRecordId(recordId, transaction);

    // MU2. Validate action
    await this.validateActivityLogSubmission(data, existingRecord, true, transaction);

    // MU3. Prepare changes
    const recordToUpdate = await this.buildSubmittedActivityLogDto(data, null, existingRecord, transaction);

    // MU4. Idempotent check
    if (await this.checkIsActivityLogSubmitted(existingRecord, transaction)) {
      return;
    }

    // MU5. Validate local copy
    if (!skipLocalCopyValidation) {
      const checks = [{ name: 'ImmutableFields', function: this.checkImmutableFieldsMatch }];
      EntityHelper.validateLocalCopy(localCopy, existingRecord, checks);
    }

    // MU6. Low-level update
    await this.updateActivityLog(who, recordToUpdate, skipLocalCopyValidation, localCopy, transaction);

    // MU7. Return
    return;
  }

  async verifyActivityLog(
    who: DataChangeEmitWho,
    recordId: number,
    data: VerifyActivityLogDataDto,
    skipLocalCopyValidation: boolean,
    localCopy: ActivityLog | null,
    existingTransaction?: DbTransaction
  ): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // MU1. Get existing record
    const existingRecord = await this.getActivityLogByRecordId(recordId, transaction);

    // MU2. Validate action
    await this.validateActivityLogVerification(data, existingRecord, true, transaction);

    // MU3. Prepare changes
    const recordToUpdate = await this.buildVerifiedActivityLogDto(data, null, existingRecord, transaction);

    // MU4. Idempotent check
    if (await this.checkIsActivityLogVerified(existingRecord, transaction)) {
      return;
    }

    // MU5. Validate local copy
    if (!skipLocalCopyValidation) {
      const checks = [{ name: 'ImmutableFields', function: this.checkImmutableFieldsMatch }];
      EntityHelper.validateLocalCopy(localCopy, existingRecord, checks);
    }

    // MU6. Low-level update
    await this.updateActivityLog(who, recordToUpdate, skipLocalCopyValidation, localCopy, transaction);

    // MU7. Return
    return;
  }

  async approveActivityLog(
    who: DataChangeEmitWho,
    recordId: number,
    data: ApproveActivityLogDataDto,
    skipLocalCopyValidation: boolean,
    localCopy: ActivityLog | null,
    existingTransaction?: DbTransaction
  ): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // MU1. Get existing record
    let existingRecord = await this.getActivityLogByRecordId(recordId, transaction);

    // MU2. Validate action
    await this.validateActivityLogApproval(data, existingRecord, true, transaction);

    // MU3. Prepare changes
    if (data.VerifyWhileApproving) {
      await this.verifyActivityLog(who, recordId, { VerifiedDate: data.ApprovedDate, VerifiedTimeZoneId: data.ApprovedTimeZoneId }, skipLocalCopyValidation, localCopy, transaction);
      existingRecord = await this.getActivityLogByRecordId(recordId, transaction);
    }
    const recordToUpdate = await this.buildApprovedActivityLogDto(data, null, existingRecord, transaction);

    // MU4. Idempotent check
    if (await this.checkIsActivityLogApproved(existingRecord, transaction)) {
      return;
    }

    // MU5. Validate local copy
    if (!skipLocalCopyValidation) {
      const checks = [{ name: 'ImmutableFields', function: this.checkImmutableFieldsMatch }];
      EntityHelper.validateLocalCopy(localCopy, existingRecord, checks);
    }

    // MU6. Low-level update
    await this.updateActivityLog(who, recordToUpdate, skipLocalCopyValidation, localCopy, transaction);

    // MU7. Return
    return;
  }

  // #endregion
  // #region Mid-level Delete

  // #endregion
  // #region Mid-level Validate Action

  async validateOpenActivityLogCreation(data: CreateOpenActivityLogDataDto, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // TODO: Check role claim

    return;
  }

  async validateActivityLogReopen(data: OpenActivityLogDataDto, existingRecord: ActivityLog, skipLowLevelValidation: boolean, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // TODO: Check role claim

    return;
  }

  async validateActivityLogSubmission(data: SubmitActivityLogDataDto, existingRecord: ActivityLog, skipLowLevelValidation: boolean, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // TODO: Check role claim

    return;
  }

  async validateActivityLogVerification(data: VerifyActivityLogDataDto, existingRecord: ActivityLog, skipLowLevelValidation: boolean, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    if (this.checkActivityLogIsUsersOwn(existingRecord)) {
      throw new ValidationException("User cannot perform this action on their own activity log.");
    }

    return;
  }

  async validateActivityLogApproval(data: ApproveActivityLogDataDto, existingRecord: ActivityLog, skipLowLevelValidation: boolean, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // TODO: Check role claim

    if (this.checkActivityLogIsUsersOwn(existingRecord)) {
      throw new ValidationException("User cannot perform this action on their own activity log.");
    }

    if (!data.VerifyWhileApproving && !(await this.checkIsActivityLogVerified(existingRecord, transaction))) {
      throw new ActionValidationException('approve', ActivityLogConstants.FRIENDLY_NAME, 'it has not been verified');
    }

    return;
  }

  async validateActivityLogReturn(data: ReturnActivityLogDataDto, existingRecord: ActivityLog, skipLowLevelValidation: boolean, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // TODO: Check role claim
    if (this.checkActivityLogIsUsersOwn(existingRecord)) {
      throw new ValidationException("User cannot perform this action on their own activity log.");
    }

    return;
  }

  // #endregion
  // #region Mid-level Validate Model

  async validateOpenActivityLog(activityLog: ActivityLog, skipLowLevelValidation: boolean, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    if (!skipLowLevelValidation) {
      await this.validateActivityLog(activityLog, transaction);
    }

    if (!(await this.checkIsActivityLogOpen(activityLog, transaction))) {
      throw new StateValidationException('Open', activityLog);
    }

    return;
  }

  async validateReturnedActivityLog(activityLog: ActivityLog, skipLowLevelValidation: boolean, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    if (!skipLowLevelValidation) {
      await this.validateActivityLog(activityLog, transaction);
    }

    if (!(await this.checkIsActivityLogReturned(activityLog, transaction))) {
      throw new StateValidationException('Returned', activityLog);
    }

    return;
  }

  async validateSubmittedActivityLog(activityLog: ActivityLog, skipLowLevelValidation: boolean, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    if (!skipLowLevelValidation) {
      await this.validateActivityLog(activityLog, transaction);
    }

    if (!(await this.checkIsActivityLogSubmitted(activityLog, transaction))) {
      throw new StateValidationException('Submitted', activityLog);
    }

    return;
  }

  async validateVerifiedActivityLog(activityLog: ActivityLog, skipLowLevelValidation: boolean, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    if (!skipLowLevelValidation) {
      await this.validateActivityLog(activityLog, transaction);
    }

    if (!(await this.checkIsActivityLogVerified(activityLog, transaction))) {
      throw new StateValidationException('Verified', activityLog);
    }

    return;
  }

  async validateApprovedActivityLog(activityLog: ActivityLog, skipLowLevelValidation: boolean, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    if (!skipLowLevelValidation) {
      await this.validateActivityLog(activityLog, transaction);
    }

    if (!(await this.checkIsActivityLogApproved(activityLog, transaction))) {
      throw new StateValidationException('Approved', activityLog);
    }

    return;
  }

  // #endregion
  // #region Mid-level Upsert

  async createOpenActivityLogOrReopen(who: DataChangeEmitWho, data: CreateOpenActivityLogDataDto, existingTransaction?: DbTransaction): Promise<CreateOpenActivityLogOrReopenDto> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const existingActivityLog = await this.getActivityLogByUserIdProjectIdDate(data.UserId, data.ProjectId, data.ActivityDate, transaction);
    if (existingActivityLog !== null) {
      if (!(await this.checkIsActivityLogOpen(existingActivityLog, transaction))) {
        await this.reopenActivityLog(who, existingActivityLog.RecordId, new OpenActivityLogDataDto(), true, null, transaction);
      }
      return {
        RecordId: existingActivityLog.RecordId,
        IsNew: false,
      };
    }
    else {
      const recordId = await this.createOpenActivityLog(who, data, transaction);
      return {
        RecordId: recordId,
        IsNew: true,
      };
    }
  }

  // #endregion
  // #region Mid-level Sync

  async updateActivityLogWithId(who: DataChangeEmitWho, recordId: number, onlineId: number, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    if (who !== DataChangeEmitWho.Sync) {
      throw new Error('Must originate from Sync.');
    }

    let recordToUpdate = await this.getActivityLogByRecordId(recordId, transaction, true);
    recordToUpdate.Id = onlineId;

    await this.updateActivityLog(who, recordToUpdate, true, null, transaction);
  }

  async upsertActivityLogFromSync(who: DataChangeEmitWho, recordToUpsert: ActivityLog, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    if (who !== DataChangeEmitWho.Sync) {
      throw new Error('Must originate from Sync.');
    }

    try {
      const existingRecord = await this.getActivityLogByOnlineId(recordToUpsert.Id, transaction, true);
      recordToUpsert.RecordId = existingRecord.RecordId;
      await this.updateActivityLog(who, recordToUpsert, true, null, transaction);
    } catch (e) {
      if (e instanceof RecordNotFoundException) {
        await this.createActivityLog(who, recordToUpsert, transaction, true);
      }
    }

    return;
  }

  // #endregion
  // #region Build

  private async buildBaseActivityLogDto(data: BaseActivityLogDataDto, existingTransaction?: DbTransaction): Promise<ActivityLog> {
    return new ActivityLog({
      UserId: data.UserId,
      ProjectId: data.ProjectId,
      ActivityDate: DateTimeHelper.formatDateTimeAsDate(data.ActivityDate),
      StatusLookupId: 0,
      IsDeleted: false,
    });
  }

  private async buildOpenActivityLogDto(
    data: OpenActivityLogDataDto,
    baseData: BaseActivityLogDataDto | null,
    existingRecord: ActivityLog | null,
    existingTransaction?: DbTransaction
  ): Promise<ActivityLog> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    if (baseData === null && existingRecord === null) {
      throw new Error('baseData and existingRecord cannot both be null.');
    }

    let record: ActivityLog = existingRecord?.clone() ?? (await this.buildBaseActivityLogDto(baseData, transaction));

    const openStatus = await this.getActivityLogStatusByNumber(ActivityLogStatusNumber.Open, transaction);
    record.setToOpen(openStatus);

    await this.validateOpenActivityLog(record, true, transaction);

    return record;
  }

  private async buildSubmittedActivityLogDto(
    data: SubmitActivityLogDataDto,
    baseData: CreateOpenActivityLogDataDto | null,
    existingRecord: ActivityLog | null,
    existingTransaction?: DbTransaction
  ): Promise<ActivityLog> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    if (baseData === null && existingRecord === null) {
      throw new Error('baseData and existingRecord cannot both be null.');
    }

    let record: ActivityLog = existingRecord?.clone() ?? (await this.buildOpenActivityLogDto(baseData as OpenActivityLogDataDto, baseData, null, transaction));

    const submittedStatus = await this.getActivityLogStatusByNumber(ActivityLogStatusNumber.Submitted, transaction);
    record.setToSubmitted(submittedStatus, this.userService.getCurrentUserId(), data.SubmittedDate, data.SubmittedTimeZoneId, data.Signature);

    await this.validateSubmittedActivityLog(record, true, transaction);

    return record;
  }

  private async buildVerifiedActivityLogDto(
    data: VerifyActivityLogDataDto,
    baseData: CreateSubmittedActivityLogDataDto | null,
    existingRecord: ActivityLog | null,
    existingTransaction?: DbTransaction
  ): Promise<ActivityLog> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    if (baseData === null && existingRecord === null) {
      throw new Error('baseData and existingRecord cannot both be null.');
    }

    let record: ActivityLog = existingRecord?.clone() ?? (await this.buildSubmittedActivityLogDto(baseData as SubmitActivityLogDataDto, baseData, null, transaction));

    const verifiedStatus = await this.getActivityLogStatusByNumber(ActivityLogStatusNumber.Verified, transaction);
    record.setToVerified(verifiedStatus, this.userService.getCurrentUserId(), data.VerifiedDate, data.VerifiedTimeZoneId, null);

    await this.validateVerifiedActivityLog(record, true, transaction);

    return record;
  }

  private async buildApprovedActivityLogDto(
    data: ApproveActivityLogDataDto,
    baseData: CreateVerifiedActivityLogDataDto | null,
    existingRecord: ActivityLog | null,
    existingTransaction?: DbTransaction
  ): Promise<ActivityLog> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    if (baseData === null && existingRecord === null) {
      throw new Error('baseData and existingRecord cannot both be null.');
    }

    let record: ActivityLog = existingRecord?.clone() ?? (await this.buildVerifiedActivityLogDto(baseData as VerifyActivityLogDataDto, baseData, null, transaction));

    const approvedStatus = await this.getActivityLogStatusByNumber(ActivityLogStatusNumber.Approved, transaction);
    record.setToApproved(approvedStatus, this.userService.getCurrentUserId(), data.ApprovedDate, data.ApprovedTimeZoneId, null);

    await this.validateApprovedActivityLog(record, true, transaction);

    return record;
  }

  private async buildReturnedActivityLogDto(
    data: ReturnActivityLogDataDto,
    baseData: CreateOpenActivityLogDataDto | null,
    existingRecord: ActivityLog | null,
    existingTransaction?: DbTransaction
  ): Promise<ActivityLog> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    if (baseData === null && existingRecord === null) {
      throw new Error('baseData and existingRecord cannot both be null.');
    }

    let record: ActivityLog = existingRecord?.clone() ?? (await this.buildOpenActivityLogDto(baseData as OpenActivityLogDataDto, baseData, null, transaction));

    const returnedStatus = await this.getActivityLogStatusByNumber(ActivityLogStatusNumber.Returned, transaction);
    record.setToReturned(returnedStatus, this.userService.getCurrentUserId(), data.ReturnedDate, data.ReturnedTimeZoneId, data.Remarks);

    await this.validateReturnedActivityLog(record, true, transaction);

    return record;
  }

  // #endregion
  // #region Check

  async checkIsActivityLogOpen(activityLog: ActivityLog, existingTransaction?: DbTransaction): Promise<boolean> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const status = await this.getActivityLogStatusById(activityLog.StatusLookupId, transaction);
    return ActivityLogStatusHierarchy.OpenStatusNumbers.includes(status.Number);
  }

  async checkIsActivityLogReturned(activityLog: ActivityLog, existingTransaction?: DbTransaction): Promise<boolean> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const status = await this.getActivityLogStatusById(activityLog.StatusLookupId, transaction);
    return ActivityLogStatusHierarchy.ReturnedStatusNumbers.includes(status.Number);
  }

  async checkIsActivityLogSubmitted(activityLog: ActivityLog, existingTransaction?: DbTransaction): Promise<boolean> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const status = await this.getActivityLogStatusById(activityLog.StatusLookupId, transaction);
    return ActivityLogStatusHierarchy.SubmittedStatusNumbers.includes(status.Number);
  }

  async checkIsActivityLogVerified(activityLog: ActivityLog, existingTransaction?: DbTransaction): Promise<boolean> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const status = await this.getActivityLogStatusById(activityLog.StatusLookupId, transaction);
    return ActivityLogStatusHierarchy.VerifiedStatusNumbers.includes(status.Number);
  }

  async checkIsActivityLogApproved(activityLog: ActivityLog, existingTransaction?: DbTransaction): Promise<boolean> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const status = await this.getActivityLogStatusById(activityLog.StatusLookupId, transaction);
    return ActivityLogStatusHierarchy.ApprovedStatusNumbers.includes(status.Number);
  }

  checkIsActivityLogSoftDeleted(activityLog: ActivityLog): boolean {
    return activityLog.IsDeleted;
  }

  checkImmutableFieldsMatch(a: ActivityLog, b: ActivityLog): boolean {
    return a.UserId === b.UserId && a.ActivityDate === b.ActivityDate && a.ProjectId === b.ProjectId;
  }

  checkAnyActivityLogIdentifiersMatch(a: ActivityLog, b: ActivityLog): boolean {
    if (!a?.RecordId && !a?.Id) {
      return false;
    }

    if (!b?.RecordId && !b?.Id) {
      return false;
    }

    return a?.RecordId === b?.RecordId || a?.Id === b?.Id;
  }

  checkActivityLogIsUsersOwn(activityLog: ActivityLog): boolean {
    return activityLog.UserId === this.userService.getCurrentUserId();
  }

  // #endregion
  // #region Lookup

  /**
   * Retrieves all activity log statuses (StatusLookups).
   * @returns A promise that resolves to an array of StatusLookup objects.
   * @throws If there was an error loading the statuses.
   */
  async getActivityLogStatuses(existingTransaction?: DbTransaction): Promise<StatusLookup[]> {
    try {
      await this.loadStatuses(existingTransaction);
      return this.statuses;
    } catch (error) {
      console.error('Failed to load statuses:', error);
      throw error;
    }
  }

  /**
   * Retrieves the activity log status (StatusLookup) by its Id.
   * @param statusId - The id of the status to retrieve.
   * @param existingTransaction - Optional. An existing database transaction to use for the operation.
   * @returns A promise that resolves to the StatusLookup object matching the provided Id.
   * @throws If there was an error retrieving the activity log statuses or if the status number is invalid.
   */
  async getActivityLogStatusById(statusId: number, existingTransaction?: DbTransaction): Promise<StatusLookup> {
    // Try to find the status from memory
    let status = this.statuses.find(status => status.Id === statusId);

    if (!status) {
      // Refresh the statuses in memory and try to find the status again
      await this.loadStatuses(existingTransaction);
      status = this.statuses.find(status => status.Id === statusId);
    }

    if (!status) {
      throw new Error(`Status with Id ${statusId} not found`);
    }

    return status;
  }

  /**
   * Retrieves the activity log status (StatusLookup) by its number.
   * @param statusNumber - The number of the activity log status.
   * @param existingTransaction - Optional. An existing database transaction to use for the operation.
   * @returns A promise that resolves to the StatusLookup object matching the provided number.
   * @throws If there was an error retrieving the activity log statuses or if the status number is invalid.
   */
  async getActivityLogStatusByNumber(statusNumber: ActivityLogStatusNumber, existingTransaction?: DbTransaction): Promise<StatusLookup> {
    // Try to find the status from memory
    let status = this.statuses.find(status => status.Number === statusNumber);

    if (!status) {
      // Refresh the statuses in memory and try to find the status again
      await this.loadStatuses(existingTransaction);
      status = this.statuses.find(status => status.Number === statusNumber);
    }

    if (!status) {
      throw new Error(`Status with number ${statusNumber} not found`);
    }

    return status;
  }

  /**
   * Retrieves the status for an activity log.
   * @param activityLog - The activity log for which to retrieve the status.
   * @param existingTransaction - Optional. An existing database transaction to use for the operation.
   * @returns A promise that resolves to the StatusLookup object.
   * @throws If there was an error retrieving the status.
   */
  async getStatusForActivityLog(activityLog: ActivityLog, existingTransaction?: DbTransaction): Promise<StatusLookup> {
    try {
      return await this.getActivityLogStatusById(activityLog.StatusLookupId, existingTransaction);
    } catch (error) {
      console.error('Failed to get status for activity log:', error);
      throw error;
    }
  }

  // #endregion
  // #region Transform

  filterActivityLogs(activityLogs: ActivityLog[], filter: ActivityLogFilterDataDto): ActivityLog[] {
    let filteredActivityLogs = activityLogs;

    if (filter.Ids) {
      filteredActivityLogs = filteredActivityLogs.filter(activityLog => filter.Ids.includes(activityLog.Id));
    }
    if (filter.userId) {
      filteredActivityLogs = filteredActivityLogs.filter(activityLog => filter.userId === activityLog.UserId);
    }
    if (filter.userIds) {
      filteredActivityLogs = filteredActivityLogs.filter(activityLog => filter.userIds.includes(activityLog.UserId));
    }
    if (filter.excludedUserId) {
      filteredActivityLogs = filteredActivityLogs.filter(activityLog => filter.excludedUserId !== activityLog.UserId);
    }
    if (filter.excludedUserIds) {
      filteredActivityLogs = filteredActivityLogs.filter(activityLog => !filter.excludedUserIds.includes(activityLog.UserId));
    }
    if (filter.projectId) {
      filteredActivityLogs = filteredActivityLogs.filter(activityLog => filter.projectId === activityLog.ProjectId);
    }
    if (filter.projectIds) {
      filteredActivityLogs = filteredActivityLogs.filter(activityLog => filter.projectIds.includes(activityLog.ProjectId));
    }
    if (filter.activityDate) {
      filteredActivityLogs = filteredActivityLogs.filter(activityLog => dayjs(activityLog.ActivityDate).isSame(dayjs(filter.activityDate), 'day'));
    }
    if (filter.activityDateMax) {
      filteredActivityLogs = filteredActivityLogs.filter(activityLog => dayjs(activityLog.ActivityDate).isSameOrBefore(dayjs(filter.activityDateMax), 'day'));
    }
    if (filter.activityDateMin) {
      filteredActivityLogs = filteredActivityLogs.filter(activityLog => dayjs(activityLog.ActivityDate).isSameOrAfter(dayjs(filter.activityDateMin), 'day'));
    }
    if (filter.isDeleted) {
      filteredActivityLogs = filteredActivityLogs.filter(activityLog => filter.isDeleted === activityLog.IsDeleted);
    }

    return filteredActivityLogs;
  }

  async getMinimumStatusForActivityLogs(activityLogs: ActivityLog[], existingTransaction?: DbTransaction): Promise<StatusLookup> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    let activityLogStatuses: Array<StatusLookup> = [];

    for (const activityLog of activityLogs) {
      const activityLogStatus = await this.getStatusForActivityLog(activityLog, transaction);
      activityLogStatuses.push(activityLogStatus);
    }

    if (activityLogStatuses.some(status => status.Number === ActivityLogStatusNumber.Open)) {
      return this.getActivityLogStatusByNumber(ActivityLogStatusNumber.Open, transaction);
    } else if (activityLogStatuses.some(status => status.Number === ActivityLogStatusNumber.Returned)) {
      return this.getActivityLogStatusByNumber(ActivityLogStatusNumber.Returned, transaction);
    } else if (activityLogStatuses.some(status => status.Number === ActivityLogStatusNumber.Submitted)) {
      return this.getActivityLogStatusByNumber(ActivityLogStatusNumber.Submitted, transaction);
    } else if (activityLogStatuses.some(status => status.Number === ActivityLogStatusNumber.Verified)) {
      return this.getActivityLogStatusByNumber(ActivityLogStatusNumber.Verified, transaction);
    } else if (activityLogStatuses.some(status => status.Number === ActivityLogStatusNumber.Approved)) {
      return this.getActivityLogStatusByNumber(ActivityLogStatusNumber.Approved, transaction);
    } else {
      throw Error('Unknown ActivityLogStatus');
    }
  }

  getUserIdForActivityLogs(activityLogs: ActivityLog[]): number {
    let userIds = [...new Set(activityLogs.map(activityLog => activityLog.UserId))];
    if (userIds.length === 1) {
      return userIds[0];
    } else if (userIds.length === 0) {
      return null;
    } else {
      throw Error('ActivityLogs belong to multiple users.');
    }
  }

  getActivityDateForActivityLogs(activityLogs: ActivityLog[]): dayjs.Dayjs {
    let activityDates = [...new Set(activityLogs.map(activityLog => activityLog.Date))];
    if (activityDates.length === 1) {
      return dayjs(activityDates[0]);
    } else if (activityDates.length === 0) {
      return null;
    } else {
      throw Error('ActivityLogs are across multiple dates.');
    }
  }

  async getRemarksForActivityLogs(activityLogs: ActivityLog[]): Promise<string> {
    if (!activityLogs?.length) {
      return '';
    }
    const returnStatus = await this.getActivityLogStatusByNumber(ActivityLogStatusNumber.Returned, null);
    const returnedActivityLogs = activityLogs.filter(activityLog => activityLog.StatusLookupId === returnStatus.Id);

    const uniqueRemarks = [...new Set(returnedActivityLogs.map(activityLog => activityLog.Remarks))];

    return uniqueRemarks.join(', ');
  }

  // #endregion
  // #region Other

  // #endregion
}
